/**
 * Account groups: one User's arrangement of their Accounts list (CONTEXT.md →
 * Account group). Personal, saved on the User (crm_users.sidebar_layout); it
 * means nothing for access, ownership or stats.
 *
 * The layout is an ordered list of sections: the User's named, coloured groups
 * and the one ungrouped place, which always exists and sits wherever the User
 * put it. Each section holds an ordered list of account ids; an account is in at
 * most one section. An account in none of them (newly added, or never arranged)
 * shows at the top of the ungrouped place. Every function here is pure: it
 * returns a new layout and never mutates its input.
 */

export const UNGROUPED_ID = 'ungrouped';

/** Mirrors SIDEBAR_GROUP_COLORS in database/crm_users_db.py. */
export const GROUP_COLORS = ['violet', 'blue', 'cyan', 'green', 'lime', 'amber', 'orange', 'red', 'pink', 'slate'] as const;
export type GroupColor = (typeof GROUP_COLORS)[number];

export const GROUP_NAME_MAX = 40;

export interface SidebarSection {
  id: string;
  /** Groups only; the ungrouped place has no name or colour. */
  name?: string;
  color?: GroupColor;
  collapsed: boolean;
  accounts: string[];
}

export interface SidebarLayout {
  sections: SidebarSection[];
}

export const isUngrouped = (section: SidebarSection) => section.id === UNGROUPED_ID;

const emptyUngrouped = (): SidebarSection => ({ id: UNGROUPED_ID, collapsed: false, accounts: [] });

/** The User's saved layout, or — never arranged yet — one built from the old flat
 *  custom order: those accounts become the ungrouped place, in that order. */
export function seedLayout(saved: SidebarLayout | null | undefined, legacyOrder: string[] | null | undefined): SidebarLayout {
  if (saved && Array.isArray(saved.sections)) {
    const sections = saved.sections.map((s) => ({ ...s, accounts: [...(s.accounts ?? [])], collapsed: !!s.collapsed }));
    if (!sections.some(isUngrouped)) sections.unshift(emptyUngrouped());
    return { sections };
  }
  return { sections: [{ ...emptyUngrouped(), accounts: [...new Set(legacyOrder ?? [])] }] };
}

/**
 * The layout as it should be saved, given the accounts the User can see now:
 * accounts that are in no section go to the top of the ungrouped place (in the
 * order given, newest first), and — when `known` is the complete set of the
 * User's accounts across both views — accounts the User can no longer reach are
 * dropped, so one that comes back later returns ungrouped.
 */
export function materialize(layout: SidebarLayout, visibleIds: string[], known?: Set<string> | null): SidebarLayout {
  const placed = new Set(layout.sections.flatMap((s) => s.accounts));
  const fresh = visibleIds.filter((id) => !placed.has(id));
  return {
    sections: layout.sections.map((s) => {
      const kept = known ? s.accounts.filter((id) => known.has(id)) : s.accounts;
      return { ...s, accounts: isUngrouped(s) ? [...fresh, ...kept] : kept };
    }),
  };
}

/** Sections with only the accounts that are in `visibleIds`, in arrangement order
 *  (accounts in no section first in the ungrouped place). */
export function arrange(layout: SidebarLayout, visibleIds: string[]): { section: SidebarSection; ids: string[] }[] {
  const visible = new Set(visibleIds);
  return materialize(layout, visibleIds).sections.map((section) => ({
    section,
    ids: section.accounts.filter((id) => visible.has(id)),
  }));
}

/** The section an account is in (the ungrouped place when it is in none). */
export function sectionOf(layout: SidebarLayout, accountId: string): string {
  return layout.sections.find((s) => s.accounts.includes(accountId))?.id ?? UNGROUPED_ID;
}

/** The first colour no group uses yet (cycling once all are taken). */
export function nextColor(layout: SidebarLayout): GroupColor {
  const used = layout.sections.map((s) => s.color);
  return GROUP_COLORS.find((c) => !used.includes(c)) ?? GROUP_COLORS[layout.sections.length % GROUP_COLORS.length]!;
}

export function cleanName(name: string): string {
  return name.trim().slice(0, GROUP_NAME_MAX);
}

/** A new, empty group at the bottom of the list. */
export function addGroup(layout: SidebarLayout, id: string, name: string): SidebarLayout {
  const group: SidebarSection = { id, name: cleanName(name), color: nextColor(layout), collapsed: false, accounts: [] };
  return { sections: [...layout.sections, group] };
}

function update(layout: SidebarLayout, id: string, change: (s: SidebarSection) => SidebarSection): SidebarLayout {
  return { sections: layout.sections.map((s) => (s.id === id ? change(s) : s)) };
}

export function renameGroup(layout: SidebarLayout, id: string, name: string): SidebarLayout {
  const clean = cleanName(name);
  if (!clean || id === UNGROUPED_ID) return layout;
  return update(layout, id, (s) => ({ ...s, name: clean }));
}

export function setGroupColor(layout: SidebarLayout, id: string, color: GroupColor): SidebarLayout {
  if (id === UNGROUPED_ID) return layout;
  return update(layout, id, (s) => ({ ...s, color }));
}

export function setCollapsed(layout: SidebarLayout, id: string, collapsed: boolean): SidebarLayout {
  return update(layout, id, (s) => ({ ...s, collapsed }));
}

/** Removes a group; its accounts go to the bottom of the ungrouped place. The
 *  ungrouped place itself cannot be deleted. */
export function deleteGroup(layout: SidebarLayout, id: string): SidebarLayout {
  const group = layout.sections.find((s) => s.id === id);
  if (!group || id === UNGROUPED_ID) return layout;
  return {
    sections: layout.sections
      .filter((s) => s.id !== id)
      .map((s) => (isUngrouped(s) ? { ...s, accounts: [...s.accounts, ...group.accounts] } : s)),
  };
}

/** Moves an account into a section, just before `beforeId` (or to the end when
 *  `beforeId` is null or not in that section). */
export function moveAccount(layout: SidebarLayout, accountId: string, toSectionId: string, beforeId: string | null): SidebarLayout {
  if (accountId === beforeId || !layout.sections.some((s) => s.id === toSectionId)) return layout;
  return {
    sections: layout.sections.map((s) => {
      const accounts = s.accounts.filter((id) => id !== accountId);
      if (s.id !== toSectionId) return accounts.length === s.accounts.length ? s : { ...s, accounts };
      const at = beforeId === null ? -1 : accounts.indexOf(beforeId);
      accounts.splice(at === -1 ? accounts.length : at, 0, accountId);
      return { ...s, accounts };
    }),
  };
}

/** Moves a section to sit just before `beforeId` (or last when null). */
export function moveSection(layout: SidebarLayout, id: string, beforeId: string | null): SidebarLayout {
  const moving = layout.sections.find((s) => s.id === id);
  if (!moving || id === beforeId) return layout;
  const rest = layout.sections.filter((s) => s.id !== id);
  const at = beforeId === null ? -1 : rest.findIndex((s) => s.id === beforeId);
  rest.splice(at === -1 ? rest.length : at, 0, moving);
  return { sections: rest };
}

/** Moves a section one place up (-1) or down (+1). */
export function shiftSection(layout: SidebarLayout, id: string, delta: -1 | 1): SidebarLayout {
  const from = layout.sections.findIndex((s) => s.id === id);
  const to = from + delta;
  if (from === -1 || to < 0 || to >= layout.sections.length) return layout;
  const sections = [...layout.sections];
  sections.splice(to, 0, ...sections.splice(from, 1));
  return { sections };
}

/** A one-off A–Z tidy of one section (not a mode: later moves are kept as made). */
export function sortSection(layout: SidebarLayout, id: string, nameOf: (accountId: string) => string): SidebarLayout {
  return update(layout, id, (s) => ({
    ...s,
    accounts: [...s.accounts].sort((a, b) => nameOf(a).toLowerCase().localeCompare(nameOf(b).toLowerCase())),
  }));
}
